import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';

export default function XlsxPreview({ url, fileName, isDark }) {
  const [wb, setWb] = useState(null);
  const [activeSheet, setActiveSheet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const c = {
    bg:         isDark ? 'var(--ink)' : '#ffffff',
    text:       isDark ? '#e8eaf2' : 'var(--ink)',
    muted:      isDark ? '#8892b0' : 'var(--slate)',
    border:     isDark ? '#374060' : 'var(--line)',
    headerBg:   isDark ? 'rgba(180,0,0,0.12)' : 'rgba(180,0,0,0.08)',
    headerText: 'var(--red)',
    rowAlt:     isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
    tabActive:  isDark ? 'rgba(180,0,0,0.15)' : 'rgba(180,0,0,0.10)',
    tabHover:   isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
  };

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true); setError(null); setWb(null); setActiveSheet(null);
      try {
        const resp = await fetch(url);
        if (!resp.ok) throw new Error(`Fetch failed: ${resp.status}`);
        const buf = await resp.arrayBuffer();
        const workbook = XLSX.read(buf, { type: 'array' });
        if (cancelled) return;
        setWb(workbook);
        setActiveSheet(workbook.SheetNames[0]);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load file');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    if (url) load();
    return () => { cancelled = true; };
  }, [url]);

  if (loading) {
    return (
      <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:'10px', color:c.muted, background:c.bg }}>
        <div style={{ fontSize:'32px' }}>📊</div>
        <div style={{ fontSize:'13px' }}>Loading spreadsheet...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:'10px', color:'#f87171', background:c.bg, padding:'20px' }}>
        <div style={{ fontSize:'32px' }}>⚠</div>
        <div style={{ fontSize:'13px' }}>Could not preview file</div>
        <div style={{ fontSize:'11px', color:c.muted }}>{error}</div>
      </div>
    );
  }

  if (!wb || !activeSheet) return null;

  const sheet = wb.Sheets[activeSheet];
  const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', blankrows: false });
  const maxRows = 50;
  const maxCols = 6;
  const displayRows = rows.slice(0, maxRows);
  const truncated = rows.length > maxRows;
  const headerRow = displayRows[0] || [];
  const bodyRows = displayRows.slice(1);
  const totalCols = Math.max(headerRow.length, ...bodyRows.map(r => r.length));
  const colCount = Math.min(totalCols, maxCols);
  const colsHidden = Math.max(0, totalCols - colCount);

  const cell = (v) => {
    if (v === null || v === undefined || v === '') return '';
    if (typeof v === 'number') return Number.isInteger(v) ? v.toLocaleString() : v.toLocaleString(undefined, { maximumFractionDigits: 2 });
    return String(v);
  };

  return (
    <div style={{ flex:1, display:'flex', flexDirection:'column', background:c.bg, overflow:'hidden', minWidth:0 }}>
      {/* Sheet tabs */}
      <div style={{ display:'flex', gap:'2px', borderBottom:`1px solid ${c.border}`, background: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.03)', overflowX:'auto', flexShrink:0 }}>
        {wb.SheetNames.map(name => {
          const active = name === activeSheet;
          return (
            <div key={name}
              onClick={() => setActiveSheet(name)}
              style={{
                padding:'8px 14px', fontSize:'12px', fontWeight: active ? '700' : '500',
                color: active ? c.headerText : c.text,
                background: active ? c.tabActive : 'transparent',
                borderBottom: active ? `2px solid ${c.headerText}` : '2px solid transparent',
                cursor:'pointer', whiteSpace:'nowrap', userSelect:'none',
                transition:'all 0.12s',
              }}
              onMouseEnter={e => { if (!active) e.currentTarget.style.background = c.tabHover; }}
              onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}>
              {name}
            </div>
          );
        })}
      </div>

      {/* Meta strip */}
      <div style={{ padding:'6px 14px', fontSize:'11px', color:c.muted, borderBottom:`1px solid ${c.border}`, display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
        <div>📄 {fileName}</div>
        <div>{wb.SheetNames.length} sheet{wb.SheetNames.length !== 1 ? 's' : ''} · {rows.length} row{rows.length !== 1 ? 's' : ''} {truncated && `(showing first ${maxRows})`}{colsHidden > 0 && ` · ${totalCols} cols (showing first ${colCount})`}</div>
      </div>

      {/* Table */}
      <div style={{ flex:1, overflow:'auto', minHeight:0 }}>
        {rows.length === 0 ? (
          <div style={{ padding:'40px', textAlign:'center', fontSize:'13px', color:c.muted }}>Empty sheet</div>
        ) : (
          <table style={{ borderCollapse:'collapse', fontSize:'12px', color:c.text, fontFamily:"'Public Sans', system-ui, sans-serif", minWidth:'100%' }}>
            <thead style={{ position:'sticky', top:0, zIndex:1 }}>
              <tr>
                {Array.from({ length: colCount }).map((_, i) => (
                  <th key={i} style={{
                    padding:'8px 12px', textAlign:'left', fontWeight:'700',
                    background:c.headerBg, color:c.headerText,
                    borderBottom:`2px solid ${c.headerText}`, borderRight:`1px solid ${c.border}`,
                    whiteSpace:'nowrap', fontSize:'11px', textTransform:'uppercase', letterSpacing:'0.05em',
                  }}>
                    {cell(headerRow[i]) || `Col ${i + 1}`}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bodyRows.map((row, ri) => (
                <tr key={ri} style={{ background: ri % 2 === 0 ? 'transparent' : c.rowAlt }}>
                  {Array.from({ length: colCount }).map((_, ci) => (
                    <td key={ci} style={{
                      padding:'6px 12px', borderBottom:`1px solid ${c.border}`, borderRight:`1px solid ${c.border}`,
                      whiteSpace:'nowrap', maxWidth:'280px', overflow:'hidden', textOverflow:'ellipsis',
                    }}>
                      {cell(row[ci])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
